import type React from 'react';
import { useState, useSyncExternalStore } from 'react';

import { cn } from '@change/ds-utils';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

import { MarqueeTrack } from './components/MarqueeTrack';
import { PlayPauseButton } from './components/PlayPauseButton';
import { useMarqueeRows } from './hooks/useMarqueeRows';
import { usePetitionMarquee } from './hooks/usePetitionMarquee';

type Props = Readonly<{
	className?: string;
}>;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Subscribes to changes in the OS reduced-motion preference, calling `onChange` whenever it
 * flips, and returns an unsubscribe function. The preference can be turned on while the page is
 * open (iOS Control Center, a Low Power or accessibility profile), so the store subscribes rather
 * than reading once - otherwise the marquee keeps running for a user who just asked everything to
 * stop moving.
 */
function subscribeToMotionPreference(onChange: () => void): () => void {
	if (typeof window !== 'object') return () => {};

	const query = window.matchMedia(REDUCED_MOTION_QUERY);
	query.addEventListener('change', onChange);

	return () => query.removeEventListener('change', onChange);
}

/**
 * Hydration-safe read of the OS motion preference: the server snapshot is `undefined`
 * (unknown) so SSR and the hydration render defer pausing to the tracks' motion-reduce CSS,
 * then React resyncs to the real client value before paint.
 */
function usePrefersReducedMotion(): boolean | undefined {
	return useSyncExternalStore<boolean | undefined>(subscribeToMotionPreference, isMotionReduced, () => undefined);
}

/**
 * Continuous petition ticker: one line on desktop, two on mobile with the top line running
 * the opposite direction. See MarqueeTrack for the loop mechanics.
 */
export const PetitionMarquee: React.FC<Props> = ({ className }) => {
	const {
		data: { petitions },
		actions: { trackPetitionClick },
	} = usePetitionMarquee();
	const prefersReducedMotion = usePrefersReducedMotion();
	// the control's explicit choice; until pressed, playback follows the motion preference, so
	// the button always announces the real state - and a reduced-motion user's explicit play wins
	const [playOverride, setPlayOverride] = useState<boolean | undefined>(undefined);
	const isPlaying = playOverride ?? (prefersReducedMotion === undefined ? undefined : !prefersReducedMotion);
	const { main, secondary } = useMarqueeRows(petitions);

	if (petitions.length === 0) return null;

	return (
		<div className={cn('flex flex-col', className)}>
			<MarqueeTrack
				petitions={secondary}
				onCardClick={trackPetitionClick}
				isPlaying={isPlaying}
				reverse
				className="md:hidden"
			/>
			<MarqueeTrack petitions={main} onCardClick={trackPetitionClick} isPlaying={isPlaying} />
			<div className="container mt-2 flex justify-end">
				{/* Attribute is in component */}
				{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
				<PlayPauseButton isPlaying={isPlaying ?? true} onClick={() => setPlayOverride(!(isPlaying ?? true))} />
			</div>
		</div>
	);
};
